'use memo';

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Suspense } from 'react';

import { NotFound } from '@/components/Error/NotFound';
import { VStack } from '@/components/Layout';

import {
  MY_CLOCKING_EMPLOYEE_ID,
  useMyClockingEmployeeId,
} from '../-hooks/useMyEmployeeId';

import { getEmployeeLatestClockingPeriodFromDb } from './-api';
import { EmployeeClockingDetailHeader } from './-components/EmployeeClockingDetailHeader';
import { useEmployeeClockingProfile } from './-query';
import { EmployeeClockingDetailSearchSchema } from './-shared/zod-schema';

export const Route = createFileRoute(
  '/_individual/individual/portal/lookup/clocking-record',
)({
  validateSearch: zodValidator(EmployeeClockingDetailSearchSchema),
  beforeLoad: async ({ search }) => {
    const y = search.year;
    const m = search.month;
    if (y != null && m != null) return;

    const latest = await getEmployeeLatestClockingPeriodFromDb(
      MY_CLOCKING_EMPLOYEE_ID,
    );
    if (!latest) return;

    throw redirect({
      to: '/individual/portal/lookup/clocking-record',
      search: { year: latest.year, month: latest.month },
      replace: true,
    });
  },
  component: EmployeeClockingDetailShell,
});

function EmployeeClockingDetailShell() {
  const employeeId = useMyClockingEmployeeId();
  const { data: profile, isPending } = useEmployeeClockingProfile(employeeId);

  if (isPending) {
    return (
      <VStack className="h-full min-h-0 min-w-0 flex-1">
        <div className="border-b border-neutral-muted-border">
          <VStack className="gap-3 bg-neutral-fill px-12 pt-4 shadow-sm">
            <div className="h-4 w-64 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-10 w-96 animate-pulse rounded bg-neutral-muted-fill" />
            <div className="h-9 w-full max-w-2xl animate-pulse rounded bg-neutral-muted-fill" />
          </VStack>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-12 pt-6">
          <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
        </div>
      </VStack>
    );
  }

  if (!profile) {
    return <NotFound />;
  }

  return (
    <VStack className="h-full min-h-0 min-w-0 flex-1">
      <EmployeeClockingDetailHeader profile={profile} />
      <Suspense
        fallback={
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-12 py-6">
            <div className="h-48 w-full animate-pulse rounded-lg bg-neutral-muted-fill" />
          </div>
        }
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-neutral-bottom-fill">
          <Outlet />
        </div>
      </Suspense>
    </VStack>
  );
}
